const bootStaffActivityTracking = () => {
    const config = window.vStaffActivity || {};

    if (!config.enabled || !config.endpoint || !config.context || typeof window.fetch !== 'function') {
        return;
    }

    const heartbeatSeconds = Math.max(5, Number(config.heartbeat_seconds || 15));
    const idleSeconds = Math.max(30, Number(config.idle_seconds || 120));
    const leaderKey = 'pono.staffActivity.activeTab';
    const tabKey = 'pono.staffActivity.tabId';
    const workflowKey = 'pono.staffActivity.workflow';
    const workflowFormSelector = 'form[data-messages-compose-form="true"], form[data-admin2026-messages-compose-form="true"]';
    const dialogSelector = '[role="dialog"][aria-modal="true"]';
    const randomId = () => {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID().replace(/-/g, '');
        }

        return String(Date.now()) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    };
    const readJson = (storage, key) => {
        try {
            return JSON.parse(storage.getItem(key) || 'null');
        } catch (error) {
            return null;
        }
    };
    const tabId = (() => {
        try {
            const existing = window.sessionStorage.getItem(tabKey);
            if (existing) {
                return existing;
            }

            const created = randomId();
            window.sessionStorage.setItem(tabKey, created);
            return created;
        } catch (error) {
            return randomId();
        }
    })();
    const pageId = randomId();
    const csrf = document.querySelector('meta[name="csrf-token"]');
    let sequence = 0;
    let lastInteractionAt = Date.now();
    let lastLeaderWriteAt = 0;
    let leaderWriteTimer = null;
    let cachedLeader = null;
    let sending = false;
    let queued = false;
    let workflowInputObserver = null;
    let trackedDialogObserver = null;
    const observedDialogs = new WeakSet();
    let activeContext = config.context;
    let queuedContext = null;
    let contextSyncQueued = false;
    let pendingOnboardingEvents = [];

    const currentWorkflow = () => {
        try {
            const workflow = readJson(window.sessionStorage, workflowKey);
            if (!workflow || !workflow.token) {
                return null;
            }

            const configClientId = Number(config.client_user_id || 0);
            const workflowClientId = Number(workflow.client_user_id || 0);
            if (configClientId > 0 && workflowClientId > 0 && configClientId !== workflowClientId) {
                window.sessionStorage.removeItem(workflowKey);
                return null;
            }

            return workflow;
        } catch (error) {
            return null;
        }
    };

    const storeWorkflow = (data) => {
        try {
            if (data && data.workflow_complete) {
                window.sessionStorage.removeItem(workflowKey);
                syncWorkflowInputs();
                return;
            }

            if (data && data.workflow_token) {
                window.sessionStorage.setItem(workflowKey, JSON.stringify({
                    token: String(data.workflow_token),
                    attempt_id: Number(data.workflow_attempt_id || 0),
                    workflow_type: String(data.workflow_type || ''),
                    client_user_id: Number(data.client_user_id || 0),
                    stored_at: Date.now(),
                }));
                syncWorkflowInputs();
            }
        } catch (error) {}
    };

    const storeContext = (data, sentContext) => {
        if (!data || !data.context || !sentContext) {
            return;
        }

        const renewedContext = String(data.context);
        if (!renewedContext || renewedContext === sentContext) {
            return;
        }

        if (config.context === sentContext) {
            config.context = renewedContext;
        }
        if (config.contexts && typeof config.contexts === 'object') {
            Object.keys(config.contexts).forEach((key) => {
                if (config.contexts[key] === sentContext) {
                    config.contexts[key] = renewedContext;
                }
            });
        }
        if (activeContext === sentContext) {
            activeContext = renewedContext;
        }
        if (queuedContext === sentContext) {
            queuedContext = renewedContext;
        }
    };

    try {
        cachedLeader = readJson(window.localStorage, leaderKey);
    } catch (error) {}

    const elementsMatchingOrWithin = (root, selector) => {
        if (!root || root.nodeType !== 1) {
            return [];
        }

        const matches = [];
        if (typeof root.matches === 'function' && root.matches(selector)) {
            matches.push(root);
        }
        if (typeof root.querySelectorAll === 'function') {
            root.querySelectorAll(selector).forEach((element) => matches.push(element));
        }

        return matches;
    };

    const syncWorkflowInputs = (root = document) => {
        const workflow = currentWorkflow();
        const forms = root === document
            ? Array.from(document.querySelectorAll(workflowFormSelector))
            : elementsMatchingOrWithin(root, workflowFormSelector);

        forms.forEach((form) => {
            let input = form.querySelector('input[name="staff_activity_workflow_token"]');

            if (!workflow || !workflow.token) {
                if (input) {
                    input.remove();
                }
                return;
            }

            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'staff_activity_workflow_token';
                form.appendChild(input);
            }

            input.value = workflow.token;
        });
    };

    const writeLeader = (force = false) => {
        const canLead = document.visibilityState === 'visible'
            && !document.hidden
            && (typeof document.hasFocus !== 'function' || document.hasFocus());
        if (!canLead) {
            return;
        }

        const now = Date.now();
        if (!force && now - lastLeaderWriteAt < 1000) {
            return;
        }

        lastLeaderWriteAt = now;
        const nextLeader = {
            tab_id: tabId,
            active_at: now,
        };
        cachedLeader = nextLeader;

        try {
            window.localStorage.setItem(leaderKey, JSON.stringify(nextLeader));
        } catch (error) {}
    };

    const queueLeaderWrite = (force = false) => {
        if (leaderWriteTimer !== null) {
            if (!force) {
                return;
            }

            window.clearTimeout(leaderWriteTimer);
        }

        const delay = force ? 0 : Math.max(0, 1000 - (Date.now() - lastLeaderWriteAt));
        leaderWriteTimer = window.setTimeout(() => {
            leaderWriteTimer = null;
            writeLeader(force);
        }, delay);
    };

    const leader = () => cachedLeader;

    const isActive = () => {
        const activeLeader = leader();
        const visible = document.visibilityState === 'visible' && !document.hidden;
        const notIdle = Date.now() - lastInteractionAt < idleSeconds * 1000;

        return visible && notIdle && (!activeLeader || activeLeader.tab_id === tabId);
    };

    const payload = (ending = false, contextToken = activeContext) => {
        const workflow = currentWorkflow();

        return {
            context: contextToken,
            tab_id: tabId,
            page_id: pageId,
            sequence: ++sequence,
            active: isActive(),
            ending: ending,
            client_active_at_ms: lastInteractionAt,
            workflow_token: workflow ? workflow.token : null,
            onboarding_events: pendingOnboardingEvents.slice(0, 20),
        };
    };

    const send = (ending = false, contextToken = activeContext) => {
        if (sending && !ending) {
            queued = true;
            queuedContext = contextToken;
            return;
        }

        sending = true;
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
        if (csrf && csrf.getAttribute('content')) {
            headers['X-CSRF-TOKEN'] = csrf.getAttribute('content');
        }

        const requestPayload = payload(ending, contextToken);
        const sentOnboardingEventIds = requestPayload.onboarding_events.map((event) => event.event_id);

        window.fetch(config.endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: ending,
            headers: headers,
            body: JSON.stringify(requestPayload),
        }).then((response) => response.ok ? response.json() : null)
            .then((data) => {
                if (data && sentOnboardingEventIds.length) {
                    pendingOnboardingEvents = pendingOnboardingEvents.filter(
                        (event) => !sentOnboardingEventIds.includes(event.event_id)
                    );
                }
                storeContext(data, contextToken);
                storeWorkflow(data);
            })
            .catch(() => {})
            .finally(() => {
                sending = false;
                if (queued) {
                    queued = false;
                    const nextContext = queuedContext || activeContext;
                    queuedContext = null;
                    send(false, nextContext);
                }
            });
    };

    const visibleDrawerContext = () => {
        if (!config.contexts || typeof config.contexts !== 'object') {
            return config.context;
        }

        const dialogs = Array.prototype.filter.call(
            document.querySelectorAll('[role="dialog"][aria-modal="true"]'),
            (dialog) => {
                if (!dialog || !dialog.querySelector('.admin2026-drawer-panel') || !dialog.getClientRects().length) {
                    return false;
                }

                const style = window.getComputedStyle(dialog);

                return style.display !== 'none' && style.visibility !== 'hidden';
            }
        );
        const dialog = dialogs.length ? dialogs[dialogs.length - 1] : null;
        if (!dialog) {
            return config.context;
        }

        const key = String(dialog.getAttribute('data-staff-activity-context-key') || 'drawer');

        return config.contexts[key] || config.contexts.drawer || config.context;
    };

    const observeTrackedDialogs = (root = document) => {
        if (!trackedDialogObserver) {
            return 0;
        }

        const dialogs = root === document
            ? Array.from(document.querySelectorAll(dialogSelector))
            : elementsMatchingOrWithin(root, dialogSelector);
        let observedCount = 0;

        dialogs.forEach((dialog) => {
            if (!dialog.querySelector('.admin2026-drawer-panel') || observedDialogs.has(dialog)) {
                return;
            }

            observedDialogs.add(dialog);
            trackedDialogObserver.observe(dialog, {
                attributes: true,
                attributeFilter: ['class', 'style', 'open', 'aria-hidden'],
            });
            observedCount++;
        });

        return observedCount;
    };

    const syncVisibleContext = () => {
        const nextContext = visibleDrawerContext();
        if (!nextContext || nextContext === activeContext) {
            return;
        }

        activeContext = nextContext;
        writeLeader(true);
        send(false, activeContext);
    };

    const queueContextSync = () => {
        if (contextSyncQueued) {
            return;
        }

        contextSyncQueued = true;
        window.requestAnimationFrame(() => {
            contextSyncQueued = false;
            syncVisibleContext();
        });
    };

    const markInteraction = () => {
        if (document.visibilityState === 'visible' && !document.hidden) {
            lastInteractionAt = Date.now();

            const activeLeader = leader();
            if (!activeLeader || activeLeader.tab_id !== tabId) {
                queueLeaderWrite(false);
            }
        }
    };

    const claimLeadership = () => {
        markInteraction();
        queueLeaderWrite(false);
    };

    ['keydown', 'wheel', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, markInteraction, {passive: true});
    });

    ['pointerdown', 'touchstart'].forEach((eventName) => {
        window.addEventListener(eventName, claimLeadership, {passive: true});
    });

    window.addEventListener('focus', () => {
        markInteraction();
        writeLeader(true);
    });

    window.addEventListener('storage', (event) => {
        if (!event || event.key !== leaderKey) {
            return;
        }

        try {
            cachedLeader = JSON.parse(event.newValue || 'null');
        } catch (error) {
            cachedLeader = null;
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !document.hidden) {
            markInteraction();
            writeLeader(true);
        }
        send(false);
    });

    document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form || String(form.tagName || '').toLowerCase() !== 'form') {
            return;
        }

        syncWorkflowInputs(form.parentNode || document);
    }, true);

    const recordOnboardingEvent = (eventName, attributes = {}) => {
        const allowed = [
            'tour_started',
            'tour_step_viewed',
            'tour_step_back',
            'tour_completed',
            'guide_step_viewed',
            'guide_step_back',
            'validation_failed',
        ];
        const normalizedName = String(eventName || '');

        if (!allowed.includes(normalizedName)) {
            return false;
        }

        const normalizeIdentifier = (value, maxLength) => String(value || '')
            .replace(/[^A-Za-z0-9._-]/g, '_')
            .slice(0, maxLength);
        pendingOnboardingEvents.push({
            event_id: randomId().slice(0, 64),
            event_name: normalizedName,
            step_key: normalizeIdentifier(attributes.step_key, 80) || null,
            control_key: normalizeIdentifier(attributes.control_key, 120) || null,
            outcome: normalizeIdentifier(attributes.outcome, 32) || null,
        });
        pendingOnboardingEvents = pendingOnboardingEvents.slice(-50);
        markInteraction();
        send(false);

        return true;
    };

    window.PonoStaffActivity = {
        currentWorkflowToken: () => {
            const workflow = currentWorkflow();
            return workflow ? workflow.token : '';
        },
        recordOnboardingEvent,
        syncContext: queueContextSync,
    };

    window.addEventListener('pagehide', () => {
        if (leaderWriteTimer !== null) {
            window.clearTimeout(leaderWriteTimer);
            leaderWriteTimer = null;
        }
        send(true);
    });
    if (typeof window.MutationObserver === 'function') {
        trackedDialogObserver = new window.MutationObserver(() => {
            queueContextSync();
        });
        observeTrackedDialogs();

        workflowInputObserver = new window.MutationObserver((mutations) => {
            let contextMayHaveChanged = false;

            mutations.forEach((mutation) => {
                Array.from(mutation.addedNodes || []).forEach((node) => {
                    if (elementsMatchingOrWithin(node, workflowFormSelector).length) {
                        syncWorkflowInputs(node);
                    }
                    if (observeTrackedDialogs(node) > 0) {
                        contextMayHaveChanged = true;
                    }
                });

                Array.from(mutation.removedNodes || []).forEach((node) => {
                    if (elementsMatchingOrWithin(node, dialogSelector).some((dialog) => dialog.querySelector('.admin2026-drawer-panel'))) {
                        contextMayHaveChanged = true;
                    }
                });
            });

            if (contextMayHaveChanged) {
                queueContextSync();
            }
        });
        workflowInputObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
    syncWorkflowInputs();
    syncVisibleContext();
    writeLeader(true);
    send(false);
    window.setInterval(() => send(false), heartbeatSeconds * 1000);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootStaffActivityTracking, {once: true});
} else {
    bootStaffActivityTracking();
}
